import React from "react";
import { Row, Col, Statistic, Tag, Typography } from "antd";
import { useIntl } from "react-intl";
import { TrophyOutlined, UserOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { IBrandEquityScoreResponse } from "../../services/nestjs-base-api/types";

const { Text } = Typography;

interface OverallScoreCardProps {
    results: IBrandEquityScoreResponse;
}

const OverallScoreCard: React.FunctionComponent<OverallScoreCardProps> = ({
    results,
}) => {
    const intl = useIntl();

    const getScoreColor = (score: number): string => {
        if (score >= 70) return "#52c41a"; // Green
        if (score >= 40) return "#faad14"; // Orange
        return "#ff4d4f"; // Red
    };

    const getScoreLevel = (score: number): string => {
        if (score >= 70) return intl.formatMessage({ id: 'brandEquity.score.excellent' });
        if (score >= 40) return intl.formatMessage({ id: 'brandEquity.score.good' });
        return intl.formatMessage({ id: 'brandEquity.score.needsImprovement' });
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleString();
    };

    const formatFilters = (): string => {
        const { filters } = results;
        if (filters.cohortIds?.length) {
            return intl.formatMessage(
                { id: 'brandEquity.filters.summary.cohorts' },
                { count: filters.cohortIds.length }
            );
        }
        if (filters.dateFrom && filters.dateTo) {
            return intl.formatMessage(
                { id: 'brandEquity.filters.summary.dateRange' },
                { 
                    from: new Date(filters.dateFrom).toLocaleDateString(),
                    to: new Date(filters.dateTo).toLocaleDateString()
                }
            );
        }
        return intl.formatMessage({ id: 'brandEquity.filters.summary.all' });
    };

    return (
        <div>
            <Row gutter={[32, 24]} align="middle">
                {/* Main Score */}
                <Col xs={24} md={8} style={{ textAlign: 'center' }}>
                    <div style={{ 
                        padding: '24px',
                        background: `linear-gradient(135deg, ${getScoreColor(results.overallBrandEquityScore || 0)}15, ${getScoreColor(results.overallBrandEquityScore || 0)}05)`,
                        borderRadius: '16px',
                        border: `2px solid ${getScoreColor(results.overallBrandEquityScore || 0)}30`
                    }}>
                        <TrophyOutlined 
                            style={{ 
                                fontSize: '32px', 
                                color: getScoreColor(results.overallBrandEquityScore || 0),
                                marginBottom: '12px'
                            }} 
                        />
                        <div style={{ fontSize: '48px', fontWeight: 'bold', color: getScoreColor(results.overallBrandEquityScore || 0) }}>
                            {(results.overallBrandEquityScore || 0).toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '16px', color: '#666', marginTop: '8px' }}>
                            {intl.formatMessage({ id: 'brandEquity.overallScore' })}
                        </div>
                        <Tag 
                            color={getScoreColor(results.overallBrandEquityScore || 0)}
                            style={{ marginTop: '12px', fontSize: '14px' }}
                        >
                            {getScoreLevel(results.overallBrandEquityScore || 0)}
                        </Tag>
                    </div>
                </Col>

                {/* Metrics */}
                <Col xs={24} md={16}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} sm={12}>
                            <Statistic
                                title={
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <UserOutlined />
                                        {intl.formatMessage({ id: 'brandEquity.totalResponses' })}
                                    </span>
                                }
                                value={results.totalResponses}
                                style={{
                                    padding: '16px',
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '12px',
                                }}
                            />
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <ClockCircleOutlined />
                                    <Text type="secondary">
                                        {intl.formatMessage({ id: 'brandEquity.calculatedAt' })}
                                    </Text>
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: '600' }}>
                                    {formatDate(results.calculatedAt)}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filter Summary */}
                    <div style={{ 
                        marginTop: '16px',
                        padding: '12px 16px',
                        backgroundColor: '#e6f7ff',
                        borderRadius: '8px',
                        border: '1px solid #91d5ff'
                    }}>
                        <Text style={{ fontSize: '14px' }}>
                            <strong>{intl.formatMessage({ id: 'brandEquity.appliedFilters' })}:</strong> {formatFilters()}
                        </Text>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default OverallScoreCard;